<template>
    <div
        ref="app"
        id="cz3"
        :class="[
            'cz3 overflow-hidden font-normal',
            {
                'cz3--loaded': loaded,
                'cz3--hide-outlines': hideOutlines,
            },
            `
              cz3--locale-${cleanedLocale}
            `,
            `
              cz3--short-locale-${shortCleanedLocale}
            `,
        ]"
        @keydown="keyDown"
        @mousedown="mouseDown"
    >
        <customizer-main />

        <transition name="cz3__fade">
            <div
                v-if="globalBusy"
                class="absolute inset-0 z-50 flex items-center justify-center bg-white/80"
            >
                <loading-spinner
                    v-if="!globalQuietBusy"
                    class="
                      size-12 text-ink

                      md:size-20
                    "
                />
            </div>
        </transition>
    </div>
</template>

<style>
    #cz3 {
        /* Highest possible z-index to ensure our content covers the client's content */
        z-index: 100000;
        position: absolute;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100%;
        overflow: hidden;
        /* baseline font weight for shadowdom */
        font-weight: 400;
    }

    html.cz3__overlay-frame, html.cz3__overlay-frame > body {
        height: 100vh;
        height: var(--cz-app-height);
        overflow: hidden;
        margin: 0;
        font-size: 16px;
    }

    .cz3__overlay-lock > *:not(.cz3):not(#tasks-container) {
        display: none !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    #cz3 .swiper-scrollbar-drag {
        @apply rounded-md h-2 bg-gray-200;
    }

    #cz3 .swiper-scrollbar-drag:hover {
        @apply bg-gray;
    }
</style>

<script>
    // eslint-disable-next-line
    import URL from 'url-polyfill';
    // eslint-disable-next-line import/extensions, no-unused-vars
    import webp from './third/webp';

    import '@netless/canvas-polyfill';

    /* eslint-disable import/no-unresolved */
    import 'swiper/css';

    import allStores from './stores/all';

    import { unload } from './helpers/cleanup';

    import CustomizerMain from './components/CustomizerMain.vue';
    import LoadingSpinner from './components/UI/LoadingSpinner.vue';

    export default {
        mixins: [allStores],

        components: {
            CustomizerMain,
            LoadingSpinner,
        },

        props: {
            options: Object,
        },

        data() {
            return {
                // Is the main UI loaded.
                loaded: false,

                // Hide outlines when using only the mouse.
                hideOutlines: true,
            };
        },

        computed: {
            /**
             * Is the UX blocked?
             */
            globalBusy() {
                return this.appStore.isBusy;
            },

            /**
             * Is the UX blocked?
             */
            globalQuietBusy() {
                return this.appStore.isQuietBusy;
            },

            /**
             * Shortcut to self-destruct flag.
             */
            selfDestruct() {
                return this.appStore.selfDestruct;
            },

            /**
             * Watch locale.
             */
            locale() {
                return this.customizerStore.options?.locale;
            },

            /**
             * Get normalized locale ID.
             */
            cleanedLocale() {
                if (this.locale) {
                    return this.locale.replace('_', '-').toLowerCase();
                }

                return 'generic';
            },

            /**
             * Get short normalized locale ID (i.e. for en-US return en only).
             */
            shortCleanedLocale() {
                const locale = this.cleanedLocale;

                return locale.split('-')[0];
            },
        },

        watch: {
            locale() {
                if (this.locale) {
                    this.$i18n.locale = this.locale;
                }
            },

            /**
             * Begin self-destruct sequence.
             */
            selfDestruct(v) {
                if (v) {
                    this.loaded = false;

                    this.$nextTick(() => {
                        this.$emit('selfDestruct');
                    });
                }
            },
        },

        beforeMount() {
            this.injectGlobalLinks();
            this.injectGlobalStyles();
        },

        mounted() {
            // Remove all page resources.
            try {
                this.restoreResources = unload();
            // eslint-disable-next-line no-unused-vars
            } catch (e) {
                // But don't crash.
            }

            // Save options in all modules.
            this.appStore.setOptions(this.options);
            this.customizerStore.setOptions(this.options);
            this.customizerWatcherStore.setOptions(this.options);

            this.configureSizing();

            // Once the popup is in DOM, load the product data.
            this.customizerStore.load({
                options: this.options,
            });

            this.loaded = true;
        },

        beforeUnmount() {
            if (this.restoreResources) {
                this.restoreResources();
            }

            this.stopResizing();
        },

        methods: {
            /**
             * Configure the resize of the widget so it always takes the full height.
             */
            configureSizing() {
                // Reset scroll position.
                this.oldBodyScroll = document.body.scrollTop || document.documentElement.scrollTop;

                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;

                document.body.style.height = this.lockHeight ? `${this.lockHeight}px` : `${this.$refs.app.offsetHeight}px`;

                // Hide everything so we have a clean page.
                document.body.classList.add('cz3__overlay-lock');
                document.documentElement.classList.add('cz3__overlay-frame');

                // Listen to resize.
                window.addEventListener('resize', this.resize);

                // And resize right away.
                this.$nextTick(() => {
                    this.resize();
                });

                // Because the scrolling state may have changed now with the body height adjusted, reevaluate sizing a moment later.
                this.heightTimer = setInterval(this.resize, 100);
            },

            /**
             * Cleanly destroy the widget.
             */
            stopResizing() {
                document.body.classList.remove('cz3__overlay-lock');
                document.documentElement.classList.remove('cz3__overlay-frame');
                document.body.style.height = 'auto';

                window.removeEventListener('resize', this.resize);

                if (this.oldBodyScroll) {
                    document.body.scrollTop = this.oldBodyScroll;
                    document.documentElement.scrollTop = this.oldBodyScroll;
                }

                if (this.heightTimer) {
                    clearInterval(this.heightTimer);
                }
            },

            /**
             * Update sizing parameters.
             */
            resize() {
                const h = window.innerHeight;

                // Don't resize if the keyboard is open.
                if (this.lastHeight !== h && !this.appStore.isKeyboardOpen) {
                    // Resize the frame.
                    this.$refs.app.style.setProperty('--cz-app-height', `${h}px`);
                    document.documentElement.style.setProperty('--cz-app-height', `${h}px`);

                    this.lastHeight = h;
                }

                this.checkIfMobile();
            },

            /**
             * See if the screen is below the lg breakpoint.
             */
            checkIfMobile() {
                this.appStore.setIsMobile(window.innerWidth < 1024);
            },

            /**
             * Hides outlines on mouse actions.
             */
            mouseDown() {
                this.hideOutlines = true;
            },

            /**
             * Shows outlines again when keyboard is engaged.
             */
            keyDown() {
                this.hideOutlines = false;
            },

            /**
             * Inject global links.
             * For example, webfonts need to be loaded at the top level.
             */
            injectGlobalLinks() {
                // Assume that fonts will be loaded from the external context.

                // const preconnectGoogleapis = document.createElement('link');
                // preconnectGoogleapis.rel = 'preconnect';
                // preconnectGoogleapis.href = 'https://fonts.googleapis.com';
                // document.head.appendChild(preconnectGoogleapis);

                // const preconnectGstatic = document.createElement('link');
                // preconnectGstatic.rel = 'preconnect';
                // preconnectGstatic.href = 'https://fonts.gstatic.com';
                // preconnectGstatic.crossOrigin = 'anonymous';
                // document.head.appendChild(preconnectGstatic);

                // const fontStylesheet = document.createElement('link');
                // fontStylesheet.rel = 'stylesheet';
                // fontStylesheet.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@100..900&display=swap';
                // document.head.appendChild(fontStylesheet);
            },

            /**
             * Inject styles back to the global document. Since we are attaching styles to the shadow root, we now have to do this manually.
             */
            injectGlobalStyles() {
                const styles = [
                    `html.cz3__overlay-frame, html.cz3__overlay-frame > body {
                        height: 100vh;
                        height: var(--cz-app-height);
                        overflow: hidden;
                        margin: 0;
                        font-size: 16px;
                    }`,

                    'body > .cz3 { display: block; }',

                    `.cz3__overlay-lock > *:not(.cz3):not(#tasks-container) {
                        display: none !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }`,
                ];

                for (const s of styles) {
                    if (s.startsWith('DISABLE')) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const style = document.createElement('style');

                    style.id = '__driveglobalstyles';

                    style.appendChild(document.createTextNode(s));

                    document.head.appendChild(style);
                }
            },
        },
    };
</script>
